import clsx from 'clsx';
import { ReactNode } from 'react';

export interface CardTabItem {
  id: string;
  isActive: boolean;
  /** A tab that leads nowhere (nothing to open yet). */
  disabled?: boolean;
  onSelect: () => void;
  /** Content of the tab; `isActive` lets it pick its colours. */
  render: (isActive: boolean) => ReactNode;
}

interface CardTabsProps {
  items: CardTabItem[];
  /** Accessible name of the group of tabs. */
  ariaLabel?: string;
}

/** Border colour of the card (gray-200), drawn by the concave corners. */
const BORDER = '#e5e7eb';

/** Concave corner joining the active tab to the card, on the given side. */
const TabCurve = ({ side }: { side: 'left' | 'right' }) => (
  <>
    <span
      aria-hidden="true"
      className={clsx('absolute -bottom-px w-0.5 h-3 bg-white', side === 'left' ? '-left-px' : '-right-px')}
    />
    <span
      aria-hidden="true"
      data-testid="card-tab-curve"
      className={clsx('absolute -bottom-px w-3 h-3', side === 'left' ? '-left-[13px]' : '-right-[13px]')}
      style={{
        background: `radial-gradient(circle at ${side === 'left' ? '0 0' : '100% 0'}, transparent 11px, ${BORDER} 11px, ${BORDER} 12px, white 12px)`
      }}
    />
  </>
);

/**
 * Browser-like tabs forming the top of a card (the card owns the border, the radius and
 * `overflow-hidden`): a grey strip over the whole width, the active tab white and merged with the
 * card through concave corners, the other tabs bare (a thin line between two of them) until hovered or focused, the end tabs glued
 * to the edges of the card. The tabs share the width; what a tab shows is up to the caller.
 */
export const CardTabs: React.FC<CardTabsProps> = ({ items, ariaLabel }) => {
  if (items.length === 0) return null;

  return (
    <div className="relative bg-gray-50 pt-2.5">
      <div role="group" aria-label={ariaLabel} className="relative z-[1] flex items-end gap-3">
        {items.map((item, index) => {
          const isFirst = index === 0;
          const isLast = index === items.length - 1;
          // A thin line between two bare tabs, as in a browser; none beside the active tab.
          const hasSeparator = !item.isActive && !isLast && !items[index + 1].isActive;

          return (
            <button
              key={item.id}
              type="button"
              disabled={item.disabled}
              aria-current={item.isActive || undefined}
              onClick={() => {
                if (!item.isActive && !item.disabled) {
                  item.onSelect();
                }
              }}
              className={clsx(
                'group relative flex-1 min-w-0 flex items-center gap-3.5 px-5 text-left outline-none transition-colors',
                item.isActive
                  ? ['h-[68px] bg-white border border-gray-200 border-b-white rounded-t-xl', isFirst && 'border-l-0', isLast && 'border-r-0']
                  : [
                    // Same top as the active tab (68px): 60px + the 8px that lift the pill off the card.
                    'h-[60px] mb-2 rounded-xl border border-transparent',
                    'enabled:hover:bg-gray-100 focus-visible:bg-gray-100 disabled:opacity-60 disabled:cursor-not-allowed',
                    // The separator gives way to the pill of either neighbour.
                    '[&:has(+button:hover)_[data-separator]]:opacity-0 [&:has(+button:focus-visible)_[data-separator]]:opacity-0',
                    isFirst && 'ml-2',
                    isLast && 'mr-2'
                  ]
              )}
            >
              {item.render(item.isActive)}

              {hasSeparator && (
                <span
                  aria-hidden="true"
                  data-separator=""
                  data-testid="card-tab-separator"
                  className="absolute -right-[7px] top-1/2 -translate-y-1/2 w-0.5 h-8 rounded-full bg-gray-300 transition-opacity group-hover:opacity-0 group-focus-visible:opacity-0"
                />
              )}
              {item.isActive && !isFirst && <TabCurve side="left" />}
              {item.isActive && !isLast && <TabCurve side="right" />}
            </button>
          );
        })}
      </div>

      {/* Top edge of the card content, covered by the active tab */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gray-200" />
    </div>
  );
};

/** Round icon at the start of a tab, in the colours of its state. */
export const CardTabIcon: React.FC<{ isActive: boolean; children: ReactNode }> = ({ isActive, children }) => (
  <span
    aria-hidden="true"
    className={clsx(
      'flex-none flex items-center justify-center w-9 h-9 rounded-full transition-colors',
      isActive ? 'bg-primary/10 text-primary' : 'bg-gray-200 text-gray-500 group-hover:bg-gray-300'
    )}
  >
    {children}
  </span>
);

/** Two lines of a tab: a small label over the main text. */
export const CardTabText: React.FC<{ isActive: boolean; eyebrow?: ReactNode; children: ReactNode }> = ({
  isActive,
  eyebrow,
  children
}) => (
  <span className="flex flex-col gap-0.5 min-w-0">
    {eyebrow && (
      <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500 truncate">{eyebrow}</span>
    )}
    <span className={clsx('text-base font-bold truncate', isActive ? 'text-gray-900' : 'text-gray-700')}>
      {children}
    </span>
  </span>
);
